import { useState, useEffect } from "react";
import { toast } from "sonner";
import { CheckCircle2, UserPlus, Loader2, AlertTriangle } from "lucide-react";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";

/**
 * Shows how an extracted invoice vendor relates to the Vendors master:
 *  - matched_vendor (GSTIN/PAN/near-exact): green, already applied
 *  - vendor_suggestion (fuzzy name + score): amber, "Use this" to confirm/override
 *  - neither, but a name was typed: offer "Create vendor" from the invoice fields,
 *    warning first if it resembles existing vendors (similar_vendors) to avoid dupes
 */
export function VendorMatchHint({ fields, setFields }) {
  const [busy, setBusy] = useState(false);
  const m = fields.matched_vendor;
  const sug = fields.vendor_suggestion;

  // When the vendor name is typed/edited by hand (hints cleared), re-run the
  // duplicate lookup so manual entries are also flagged against the master.
  useEffect(() => {
    const nm = (fields.vendor || "").trim();
    if (nm.length < 3 || fields.matched_vendor || fields.vendor_suggestion
        || (fields.similar_vendors && fields.similar_vendors.length)) return;
    let cancelled = false;
    const t = setTimeout(async () => {
      try {
        const { data } = await api.get("/vendors/similar", { params: { name: nm } });
        if (cancelled) return;
        const sim = data.similar_vendors || [];
        const sg = data.vendor_suggestion || data.matched_vendor || null;
        if (!sg && sim.length === 0) return;
        const merged = sg && !sim.find((s) => s.id === sg.id) ? [sg, ...sim] : sim;
        setFields((p) => ((p.vendor || "").trim() === nm
          ? { ...p, vendor_suggestion: sg, similar_vendors: merged }
          : p));
      } catch (e) { /* non-blocking hint */ }
    }, 400);
    return () => { cancelled = true; clearTimeout(t); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fields.vendor]);

  const linkTo = (v) => {
    setFields((p) => ({
      ...p,
      vendor: v.name,
      category: (!p.category || p.category === "Other") && v.category ? v.category : p.category,
      currency: v.currency && (!p.currency || p.currency === "INR") ? v.currency : p.currency,
      tds_rate: v.tds_rate && !(Number(p.tds_rate) > 0) ? v.tds_rate : p.tds_rate,
      tds_section: v.tds_rate && !(Number(p.tds_rate) > 0) ? (v.tds_section || "") : p.tds_section,
      matched_vendor: { ...v, applied: true },
      vendor_suggestion: undefined,
      similar_vendors: undefined,
    }));
    toast.success(`Linked to ${v.name}`);
  };

  const createVendor = async () => {
    const name = (fields.vendor || "").trim();
    if (!name) return toast.error("Enter a vendor name first");
    setBusy(true);
    try {
      const { data } = await api.post("/entities/vendors", {
        name,
        gstin: fields.gstin || "",
        pan: fields.pan || "",
        category: fields.category && fields.category !== "Other" ? fields.category : "",
        currency: fields.currency || "INR",
      });
      setFields((p) => ({
        ...p,
        matched_vendor: { id: data.id, name, matched_by: "created", score: 100, applied: true },
        vendor_suggestion: undefined,
        similar_vendors: undefined,
      }));
      toast.success(`Vendor "${name}" added to master`);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not create vendor");
    } finally {
      setBusy(false);
    }
  };

  if (m) {
    return (
      <p className="mt-1 text-[11px] text-success flex items-center gap-1" data-testid="vendor-match-applied">
        <CheckCircle2 className="h-3 w-3 shrink-0" />
        {m.matched_by === "created"
          ? "Added to Vendors master"
          : `Matched to “${m.name}” by ${m.matched_by}${m.matched_by === "name" ? ` (${m.score}%)` : ""} — category & currency pre-filled`}
      </p>
    );
  }
  // Duplicate auto-flag: any existing vendors that resemble the typed name.
  // Merge the single `vendor_suggestion` into this list so the best match is
  // always included even if similar_vendors was trimmed.
  const seen = new Set();
  const similar = [];
  // Always keep the backend's best pick (even at a 50-59% score); filter only
  // the extra similar_vendors by the 60% duplicate cut-off.
  if (sug && sug.id) {
    seen.add(sug.id);
    similar.push(sug);
  }
  (fields.similar_vendors || []).forEach((s) => {
    if (s && (s.score || 0) >= 60 && s.id && !seen.has(s.id)) {
      seen.add(s.id);
      similar.push(s);
    }
  });
  similar.sort((a, b) => (b.score || 0) - (a.score || 0));
  const typed = (fields.vendor || "").trim();

  if (similar.length > 0) {
    return (
      <div className="mt-1 rounded border border-warning/30 bg-warning/10 px-2 py-1.5 text-[11px] space-y-1.5" data-testid="vendor-dup-warning">
        <p className="flex items-center gap-1 text-warning font-medium">
          <AlertTriangle className="h-3 w-3 shrink-0" />
          {similar.length === 1 ? "Looks like an existing vendor" : `Looks like ${similar.length} existing vendors`} — link it instead of adding a duplicate:
        </p>
        <div className="flex flex-wrap gap-1">
          {similar.map((s) => (
            <Button key={s.id} size="sm" variant="outline" className="h-6 px-2 text-[11px]" onClick={() => linkTo(s)} data-testid={`vendor-dup-use-${s.id}`}>
              Use “{s.name}” ({s.score}%)
            </Button>
          ))}
        </div>
        {typed && (
          <div className="flex items-center justify-between gap-2 pt-0.5 text-muted-foreground">
            <span>Genuinely a new vendor?</span>
            <Button size="sm" variant="ghost" className="h-6 px-2 text-[11px] gap-1 shrink-0" onClick={createVendor} disabled={busy} data-testid="vendor-create-btn">
              {busy ? <Loader2 className="h-3 w-3 animate-spin" /> : <UserPlus className="h-3 w-3" />} Create anyway
            </Button>
          </div>
        )}
      </div>
    );
  }
  if (typed) {
    return (
      <div className="mt-1 flex items-center justify-between gap-2 text-[11px] text-muted-foreground" data-testid="vendor-not-found">
        <span>Not in Vendors master yet</span>
        <Button size="sm" variant="outline" className="h-6 px-2 text-[11px] gap-1 shrink-0" onClick={createVendor} disabled={busy} data-testid="vendor-create-btn">
          {busy ? <Loader2 className="h-3 w-3 animate-spin" /> : <UserPlus className="h-3 w-3" />} Create vendor
        </Button>
      </div>
    );
  }
  return null;
}
